import React, { useState } from 'react';
import { ImageIcon, UploadIcon, XIcon, Maximize2Icon } from 'lucide-react';
import type { PM } from './useProductManager';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
interface CoverImagesSectionProps {
  pm: PM;
}
export const CoverImagesSection: React.FC<CoverImagesSectionProps> = ({
  pm
}) => {
  const dk = useDarkClasses();
  const {
    coverFront,
    setCoverFront,
    coverSpine,
    setCoverSpine,
    coverBack,
    setCoverBack
  } = pm;
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const handleFileChange = (type: 'front' | 'spine' | 'back', file: File) => {
    if (type === 'front') setCoverFront(file);
    if (type === 'spine') setCoverSpine(file);
    if (type === 'back') setCoverBack(file);
  };
  const handleRemove = (type: 'front' | 'spine' | 'back', e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (type === 'front') setCoverFront(null);
    if (type === 'spine') setCoverSpine(null);
    if (type === 'back') setCoverBack(null);
  };
  const hasAnyCover = coverFront || coverSpine || coverBack;
  return <div className={`p-6 rounded-xl shadow-sm ${dk.card}`} data-id="element-20998" data-mp="202h4eja7u98l">
      <div className="flex items-center justify-between mb-4" data-id="element-20999" data-mp="1m26cdcx66olu">
        <h2 className={`text-lg font-bold flex items-center ${dk.textHeading}`} data-id="element-21000" data-mp="1m3zjsuflxcuw">
          <ImageIcon className="w-5 h-5 mr-2 text-purple-500" data-id="element-21001" data-mp="1r1j86n60r60k" />
          Cover Images
        </h2>
        {hasAnyCover && <button onClick={() => setShowPreviewModal(true)} className="text-sm bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 px-3 py-1.5 rounded-md hover:bg-purple-200 dark:hover:bg-purple-900/50 flex items-center transition-colors" data-id="element-21002" data-mp="2esy33uvgul1s">
            <Maximize2Icon className="w-4 h-4 mr-1" data-id="element-21003" data-mp="29xr8dck6cqp0" /> Preview
          </button>}
      </div>

      <p className={`text-xs mb-4 ${dk.textMuted}`} data-id="element-21004" data-mp="2xnlgxhxgnt3g">
        PNG, JPG up to 10MB. These are your book-level covers. Edition-specific
        variants can be added in Editions & Formats.
      </p>

      <div className="grid grid-cols-[2fr_0.5fr_2fr] gap-3" data-id="element-21005" data-mp="3ca2a6aq3hz7m">
        {([{
        key: 'front',
        label: 'Front Cover',
        aspect: 'aspect-[2/3]',
        file: coverFront
      }, {
        key: 'spine',
        label: 'Spine',
        aspect: '',
        file: coverSpine
      }, {
        key: 'back',
        label: 'Back Cover',
        aspect: 'aspect-[2/3]',
        file: coverBack
      }] as const).map(({
        key,
        label,
        aspect,
        file
      }) => <div key={key} data-id="element-21006" data-mp="2eomuxtwsp8d3">
            <p className={`text-xs font-medium mb-1 text-center ${dk.textSecondary}`} data-id="element-21007" data-mp="2epfqrzu7udgg">
              {label}
            </p>
            <div className={`relative ${aspect || 'h-full min-h-[150px]'}`} data-id="element-21008" data-mp="29r4i3sfnq8zt">
              <label className={`block w-full h-full border-2 border-dashed rounded-lg cursor-pointer overflow-hidden transition-colors relative group ${dk.border} hover:border-purple-500`} data-id="element-21009" data-mp="25ohw485o3iaj">
                {file ? <>
                    <img src={URL.createObjectURL(file)} alt={label} className="w-full h-full object-cover" data-id="element-21010" data-mp="u7nerywx8m1g" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-id="element-21011" data-mp="1iq005rx57dg">
                      <span className="text-white text-xs font-medium" data-id="element-21012" data-mp="bfule3dxwyt7">
                        Change
                      </span>
                    </div>
                  </> : <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-21013" data-mp="3ewkx8yw7e62t">
                    <UploadIcon className="w-5 h-5 text-gray-400 mb-1" data-id="element-21014" data-mp="3jvo2okg3iebb" />
                    <span className="text-[10px] text-gray-500 dark:text-gray-400" data-id="element-21015" data-mp="3jtthr64tqm78">Upload</span>
                  </div>}
                <input type="file" className="sr-only" accept="image/*" onChange={e => {
              if (e.target.files?.[0]) {
                handleFileChange(key, e.target.files[0]);
              }
            }} data-id="element-21016" data-mp="1le43ptab3w66" />
              </label>
              {file && <button onClick={e => handleRemove(key, e)} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-sm hover:bg-red-600 transition-colors z-10" title={`Remove ${label.toLowerCase()}`} data-id="element-21017" data-mp="2959kqbrzgui2">
                  <XIcon className="w-3 h-3" data-id="element-21018" data-mp="npajrxkvek11" />
                </button>}
            </div>
          </div>)}
      </div>

      {/* Preview Modal */}
      {showPreviewModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setShowPreviewModal(false)} data-id="element-21019" data-mp="2ue2q8rzkes7g">
          <div className="relative max-w-5xl w-full max-h-[90vh] flex flex-col items-center justify-center" onClick={e => e.stopPropagation()} data-id="element-21020" data-mp="2lmkagi6j6eew">
            <button onClick={() => setShowPreviewModal(false)} className="absolute -top-10 right-0 text-white hover:text-gray-300 flex items-center" data-id="element-21021" data-mp="2bnutcrmgkxzp">
              <XIcon className="w-6 h-6 mr-1" data-id="element-21022" data-mp="2gkl9fey9jtl4" /> Close
            </button>

            <div className="flex items-stretch justify-center h-full max-h-[80vh] w-full bg-white dark:bg-gray-900 p-8 rounded-xl shadow-2xl" data-id="element-21023" data-mp="22ozjn5k7h5rn">
              {/* Back Cover */}
              <div className="w-[40%] border-r border-gray-200 dark:border-gray-700 flex items-center justify-center bg-gray-50 dark:bg-gray-800" data-id="element-21024" data-mp="x4pw4xwnlxp">
                {coverBack ? <img src={URL.createObjectURL(coverBack)} alt="Back Cover" className="max-w-full max-h-full object-contain shadow-md" data-id="element-21025" data-mp="1dulnsjiya59q" /> : <div className="text-gray-400 text-sm" data-id="element-21026" data-mp="14txbpud4aezh">No Back Cover</div>}
              </div>

              {/* Spine */}
              <div className="w-[10%] border-r border-gray-200 dark:border-gray-700 flex items-center justify-center bg-gray-100 dark:bg-gray-700" data-id="element-21027" data-mp="1wkl8yvbo707v">
                {coverSpine ? <img src={URL.createObjectURL(coverSpine)} alt="Spine" className="max-w-full max-h-full object-contain shadow-md" data-id="element-21028" data-mp="1sllyywb30d9k" /> : <div className="text-gray-400 text-xs rotate-90 whitespace-nowrap" data-id="element-21029" data-mp="2g92d8v1ii7hi">
                    No Spine
                  </div>}
              </div>

              {/* Front Cover */}
              <div className="w-[40%] flex items-center justify-center bg-gray-50 dark:bg-gray-800" data-id="element-21030" data-mp="2iua547s8qex7">
                {coverFront ? <img src={URL.createObjectURL(coverFront)} alt="Front Cover" className="max-w-full max-h-full object-contain shadow-md" data-id="element-21031" data-mp="2nufqx7btx77d" /> : <div className="text-gray-400 text-sm" data-id="element-21032" data-mp="31smg7z2ppyvf">No Front Cover</div>}
              </div>
            </div>
            <p className="text-white mt-4 text-sm" data-id="element-21033" data-mp="36pwd1zsflcta">Flat Spread Preview</p>
          </div>
        </div>}
    </div>;
};